import React, { ChangeEvent, Component, ReactNode } from 'react';
import { FormicaProps, RouteProps, withFormica } from '../../../../../helpers/withFormica';
import CustomMultiSelect from '../../../../custom/CustomMultiSelect';
import { MultiSelectChangeParams } from 'primereact/multiselect';
import RoleHelper from '../../../../../RoleHelper';
import DataType from '../../../../../constant/DataType';
import FormicaSearchSelectBox, { FormicaSelectBoxItem } from '../../../../custom/FormicaSearchSelectBox';
import FormicaInput from '../../../../custom/FormicaInput';
import { currency } from '../../../../../util/Currency';
import { RootState } from '../../../../../redux/store';
import { ConfigurationState } from '../../../../../redux/reducers/ConfigurationReducer';
import { changeConfiguration } from '../../../../../redux/actions/ConfigurationActions';
import { changePageFilter } from '../../../../../redux/actions/PageActions';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import DomainConfigurationAdditionalChannelComponent from '../channel/DomainConfigurationAdditionalChannelComponent';
import { InputSwitch } from 'primereact/inputswitch';
import ComponentName from '../../../../../constant/ComponentName';
import { query, repository } from '@trlogic/client-js';
import CollectionNames from '../../../../../constant/CollectionNames';
import { InstanceContainer } from '../../../../../config/binding/InstanceBinding';
import Injectables from '../../../../../constant/Injectables';
import { User } from '../../../../../domain/rbac/RbacDomains';
import TimePickerContainer from '../../../../custom/custom-time-picker/TimePickerContainer';
import { FormicaTimePickerDate } from '../../../../custom/custom-time-picker/TimePickerDomain';
import GeneralConfigurationItemContainer from './GeneralConfigurationItemContainer';
import LanguageUtil from '../../../../../util/LanguageUtil';
import empty = query.empty;

class DomainConfigurationGeneralComponent extends Component<Props, State> {
  public state: State = new State();
  private repository: repository.ReadOnlyQueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);

  public render(): ReactNode {
    const {
      configuration: { configuration, isUpdate },
    } = this.props;

    return (
      <div className={'w-full h-full flex flex-col px-5 scrollable-editor-container overflow-scroll pb-5'}>
        <GeneralConfigurationItemContainer
          header={LanguageUtil.get({ id: 'common.label.channel', defaultMessage: 'Channel' })}
          description={LanguageUtil.get({
            id: 'pages.configuration.general.channel.description',
            defaultMessage: 'Channel',
          })}
        >
          <span className={'flex w-full'}>
            <FormicaSearchSelectBox
              placeholder={LanguageUtil.get({ id: 'common.input.placeholder.channel', defaultMessage: 'Channel' })}
              rounded
              disabled={!RoleHelper.canEdit('CaseConfiguration') || isUpdate}
              options={this.getTransactionSelectOptions()}
              value={configuration?.dataSource}
              onChange={(e: FormicaSelectBoxItem) => this.onSelectTransaction(e)}
              data-testid={`${ComponentName.CONFIGURATION}_channel_select`}
            />
          </span>
        </GeneralConfigurationItemContainer>

        <GeneralConfigurationItemContainer
          header={LanguageUtil.get({ id: 'common.label.keyField', defaultMessage: 'Key Field' })}
          description={LanguageUtil.get({
            id: 'pages.configuration.general.keyField.description',
            defaultMessage:
              'Select the transaction field that will provide the association between different cases. This field required for the historical score analysis indicator, report and previous historical transactions to appear related to the cases created by it.',
          })}
        >
          <span className={'flex w-full'}>
            <FormicaSearchSelectBox
              placeholder={LanguageUtil.get({ id: 'common.input.placeholder.keyField', defaultMessage: 'Key Field' })}
              rounded
              showClear
              options={this.getKeyFieldSelectOptions()}
              value={configuration?.keyField}
              disabled={!RoleHelper.canEdit('CaseConfiguration')}
              onChange={(e: FormicaSelectBoxItem) => this.onSelectKeyField(e)}
              data-testid={`${ComponentName.CONFIGURATION}_key_select`}
            />
          </span>
        </GeneralConfigurationItemContainer>

        <GeneralConfigurationItemContainer
          header={LanguageUtil.get({ id: 'common.label.chooseList', defaultMessage: 'Choose List' })}
          description={LanguageUtil.get({
            id: 'pages.configuration.general.chooseList.description',
            defaultMessage:
              'Select the lists to be used in this case domain. List operations such as insert, update and delete can only be performed on the lists selected below.',
          })}
        >
          <span className={'flex w-[calc(100%-560px)]'}>
            <CustomMultiSelect
              items={this.getListSelectOptions()}
              placeholder={LanguageUtil.get({ id: 'common.input.placeholder.chooseList', defaultMessage: 'Choose List' })}
              selectedItems={configuration?.blacklists}
              label={LanguageUtil.get({ id: 'common.label.lists' })}
              disabled={!RoleHelper.canEdit('CaseConfiguration')}
              onChange={(e: MultiSelectChangeParams) => this.onSelectListsField(e)}
              data-testid={`${ComponentName.CONFIGURATION}_lists_select`}
            />
          </span>
        </GeneralConfigurationItemContainer>

        <GeneralConfigurationItemContainer
          header={LanguageUtil.get({ id: 'common.label.channelAmountField', defaultMessage: 'Channel Amount Field' })}
          description={LanguageUtil.get({
            id: 'pages.configuration.general.channelAmountField.description',
            defaultMessage: "Enter the Transaction amount field. 'Total amount' and 'Total fraud attempt cost' metrics will be calculated by this field.",
          })}
        >
          <div className={'flex w-full gap-x-4'}>
            <span className={'flex flex-col w-full'}>
              <FormicaSearchSelectBox
                options={this.getTransactionAmountFieldOptions()}
                placeholder={LanguageUtil.get({ id: 'common.input.placeholder.transactionAmountField', defaultMessage: 'Transaction Amount Field' })}
                value={configuration?.transactionAmountField}
                className={'w-2/6 h-[34px]'}
                rounded
                showClear
                disabled={!RoleHelper.canEdit('CaseConfiguration')}
                onChange={this.onChangeTransactionAmountField}
                data-testid={`${ComponentName.CONFIGURATION}_tx_amount_select`}
              />
            </span>
            <span className={'flex flex-col w-full'}>
              <FormicaSearchSelectBox
                options={this.getTransactionCurrencyFieldOptions()}
                value={configuration?.transactionCurrencyField}
                placeholder={LanguageUtil.get({ id: 'common.input.placeholder.currency', defaultMessage: 'Currency' })}
                className={'w-2/6 h-[34px]'}
                rounded
                showClear
                disabled={!RoleHelper.canEdit('CaseConfiguration')}
                onChange={this.onChangeTransactionCurrency}
                data-testid={`${ComponentName.CONFIGURATION}_currency_select`}
              />
            </span>
          </div>
        </GeneralConfigurationItemContainer>

        <GeneralConfigurationItemContainer
          header={LanguageUtil.get({ id: 'common.label.timeToFirstAssignment', defaultMessage: 'Time to First Assignment SLA (Minute)' })}
          description={LanguageUtil.get({
            id: 'pages.configuration.general.timeToFirstAssignment.description',
            defaultMessage:
              'Maximum acceptable duration for the first assignment to the case. If this duration is breached, a notification is sent to the administrators. It can also be seen on the case table.',
          })}
        >
          <span className={'flex w-full gap-x-4'}>
            <FormicaInput
              className={'h-[34px]'}
              disabled={!RoleHelper.canEdit('CaseConfiguration')}
              placeholder={LanguageUtil.get({ id: 'common.input.placeholder.timeToFirstAssignment', defaultMessage: 'Time to First Assignment SLA (Minute)' })}
              type='number'
              inputMode='numeric'
              value={configuration?.sla?.timeToFirstAssign?.value}
              onChange={this.onChangeSlaTimeToFirstAssign}
              data-testid={`${ComponentName.CONFIGURATION}_sla_tfa_input`}
            />
          </span>
        </GeneralConfigurationItemContainer>

        <GeneralConfigurationItemContainer
          header={LanguageUtil.get({ id: 'common.label.timeToDone', defaultMessage: 'Time to Done SLA (Minute)' })}
          description={LanguageUtil.get({
            id: 'pages.configuration.general.timeToDone.description',
            defaultMessage:
              'Maximum acceptable duration for the case to be closed. If this duration is breached, a notification is sent to the administrators. It can also be seen on the case table.',
          })}
        >
          <span className={'flex w-full gap-x-4'}>
            <FormicaInput
              className={'h-[34px]'}
              disabled={!RoleHelper.canEdit('CaseConfiguration')}
              placeholder={LanguageUtil.get({ id: 'common.input.placeholder.timeToDone', defaultMessage: 'Time to Done SLA (Minute)' })}
              type='number'
              inputMode='numeric'
              value={configuration?.sla?.timeToDone?.value}
              onChange={this.onChangeSlaTimeToDone}
              data-testid={`${ComponentName.CONFIGURATION}_sla_td_input`}
            />
          </span>
        </GeneralConfigurationItemContainer>

        <GeneralConfigurationItemContainer
          header={LanguageUtil.get({ id: 'common.label.bulkCloseRestrictedUsers', defaultMessage: 'Bulk Close Restricted Users' })}
          description={LanguageUtil.get({
            id: 'pages.configuration.general.bulkCloseRestrictedUsers.description',
            defaultMessage: 'Selected users cannot perform bulk close operations on the transaction page. Bulk close buttons are hidden for these users.',
          })}
        >
          <span className={'flex w-[calc(100%-560px)]'}>
            <CustomMultiSelect
              items={this.getUserOptions()}
              placeholder={LanguageUtil.get({ id: 'common.input.placeholder.users', defaultMessage: 'Users' })}
              selectedItems={configuration?.bulkCloseRestrictedUsers ?? []}
              label={LanguageUtil.get({ id: 'common.label.users', defaultMessage: 'Users' })}
              disabled={!RoleHelper.canEdit('CaseConfiguration')}
              onChange={(e: MultiSelectChangeParams) => this.onSelectBulkCloseRestrictedUsers(e)}
              data-testid={`${ComponentName.CONFIGURATION}_bulk_close_restricted_users_select`}
            />
          </span>
        </GeneralConfigurationItemContainer>

        <GeneralConfigurationItemContainer
          header={LanguageUtil.get({ id: 'common.label.forceToUseAction', defaultMessage: 'Force to use Action' })}
          description={LanguageUtil.get({
            id: 'pages.configuration.general.forceToUseAction.description',
            defaultMessage: 'If this flag is enabled, the use of action is mandatory when closing the transaction in review.',
          })}
        >
          <span className={'flex w-full gap-x-4'}>
            <InputSwitch
              checked={configuration?.actionRequired}
              onChange={(e) => this.onChangeActionRequired(e.value)}
              data-testid={`${ComponentName.CONFIGURATION}_action_required_switch`}
            />
          </span>
        </GeneralConfigurationItemContainer>

        <GeneralConfigurationItemContainer
          header={LanguageUtil.get({ id: 'common.label.forceToSelectReason', defaultMessage: 'Force to select Reason' })}
          description={LanguageUtil.get({
            id: 'pages.configuration.general.forceToSelectReason.description',
            defaultMessage: 'If this flag is enabled, the selection of reason is mandatory when closing the transaction in review.',
          })}
        >
          <span className={'flex w-full gap-x-4'}>
            <InputSwitch
              checked={configuration?.reasonRequired}
              onChange={(e) => this.onChangeReasonRequired(e.value)}
              data-testid={`${ComponentName.CONFIGURATION}_reason_required_switch`}
            />
          </span>
        </GeneralConfigurationItemContainer>

        <GeneralConfigurationItemContainer
          header={LanguageUtil.get({ id: 'common.label.allowMemberCaseAssign', defaultMessage: 'Allow members to assign/unassign alerts' })}
          description={LanguageUtil.get({
            id: 'pages.configuration.general.allowMemberCaseAssign.description',
            defaultMessage:
              'When enabled, selected users who also have case edit permission can assign alerts to another eligible user or System, and can unassign regardless of the current assignee. Admins retain full control. When disabled, the user list is cleared and only administrators can change assignees. Configurations enabled before a user list was saved keep the previous self-assign behavior until users are selected.',
          })}
        >
          <div className={'flex items-center w-[calc(100%-620px)] gap-x-4'}>
            <div className={'flex-shrink-0'}>
              <InputSwitch
                checked={configuration?.allowAssign === true}
                onChange={(e) => this.onChangeAllowAssign(e.value)}
                disabled={!RoleHelper.canEdit('CaseConfiguration')}
                data-testid={`${ComponentName.CONFIGURATION}_allow_member_assign_switch`}
              />
            </div>
            {configuration?.allowAssign === true && (
              <div className={'w-full flex-shrink-0'}>
                <CustomMultiSelect
                  items={this.getUserOptions()}
                  placeholder={LanguageUtil.get({ id: 'common.input.placeholder.users', defaultMessage: 'Users' })}
                  selectedItems={configuration?.allowAssignUsers ?? []}
                  label={LanguageUtil.get({ id: 'common.label.users', defaultMessage: 'Users' })}
                  disabled={!RoleHelper.canEdit('CaseConfiguration')}
                  onChange={(e: MultiSelectChangeParams) => this.onSelectAllowAssignUsers(e)}
                  data-testid={`${ComponentName.CONFIGURATION}_allow_member_assign_select`}
                />
              </div>
            )}
          </div>
        </GeneralConfigurationItemContainer>

        <GeneralConfigurationItemContainer
          header={LanguageUtil.get({ id: 'common.label.autoAssign', defaultMessage: 'Auto Assign' })}
          description={LanguageUtil.get({
            id: 'pages.configuration.general.autoAssign.description',
            defaultMessage: 'Allows transactions to be automatically assigned to selected users.',
          })}
        >
          <div className={'flex items-center w-[calc(100%-620px)] gap-x-4'}>
            <div className={'flex-shrink-0'}>
              <InputSwitch
                checked={configuration?.assignmentCandidatesRequired}
                onChange={(e) => this.onChangeAssignmentCandidatesRequired(e.value)}
                data-testid={`${ComponentName.CONFIGURATION}_assignment-candidates_required_switch`}
              />
            </div>
            {configuration.assignmentCandidatesRequired && (
              <div className={'w-full flex-shrink-0'}>
                <CustomMultiSelect
                  items={this.getUserOptions()}
                  placeholder={LanguageUtil.get({ id: 'common.input.placeholder.users', defaultMessage: 'Users' })}
                  selectedItems={configuration?.assignmentCandidates.map((assignmentCandidate) => assignmentCandidate.username)}
                  label={LanguageUtil.get({ id: 'common.label.users', defaultMessage: 'Users' })}
                  disabled={!RoleHelper.canEdit('CaseConfiguration')}
                  onChange={(e: MultiSelectChangeParams) => this.onSelectUsers(e)}
                  data-testid={`${ComponentName.CONFIGURATION}_assignment-candidates_select`}
                />
              </div>
            )}
          </div>
        </GeneralConfigurationItemContainer>

        <GeneralConfigurationItemContainer
          header={LanguageUtil.get({ id: 'common.label.graphDefaultDateFilter', defaultMessage: 'Graph Default Date Filter' })}
          description={LanguageUtil.get({
            id: 'pages.configuration.general.graphDefaultDateFilter.description',
            defaultMessage: 'If this flag is enabled, the graph is drawn according to the transactions in the specified time range.',
          })}
        >
          <div className={'flex items-center w-[calc(100%-620px)] gap-x-4'}>
            <div className={'flex-shrink-0'}>
              <InputSwitch
                checked={configuration?.graphDefaultFilterRequired}
                onChange={(e) => this.onChangeGraphDefaultFilterRequired(e.value)}
                data-testid={`${ComponentName.CONFIGURATION}_assignment-candidates_required_switch`}
              />
            </div>
            {configuration.graphDefaultFilterRequired && (
              <div className={'w-full flex-shrink-0 flex gap-x-2'}>
                <FormicaSearchSelectBox
                  options={this.getGraphFieldOptions()}
                  placeholder={LanguageUtil.get({ id: 'common.input.placeholder.transactionAmountField', defaultMessage: 'Transaction Amount Field' })}
                  value={configuration?.graphDefaultFilter?.field}
                  className={'h-[34px]'}
                  rounded
                  showClear
                  disabled={!RoleHelper.canEdit('CaseConfiguration')}
                  onChange={this.onChangeGraphField}
                />
                <TimePickerContainer
                  startDate={configuration?.graphDefaultFilter.startDate || { value: 0 }}
                  endDate={configuration?.graphDefaultFilter.endDate || { value: 0 }}
                  positionTop={true}
                  onChangeStartDate={this.onChangeStartDate}
                  onChangeEndDate={this.onChangeEndDate}
                />
              </div>
            )}
          </div>
        </GeneralConfigurationItemContainer>

        <GeneralConfigurationItemContainer
          header={LanguageUtil.get({ id: 'common.label.nextCaseStrategy', defaultMessage: 'Next Case Strategy' })}
          description={LanguageUtil.get({
            id: 'pages.configuration.general.nextCaseStrategy.description',
            defaultMessage:
              'When the next case button is pressed on the transactions page, it determines whether the oldest transactions or the transactions with the highest score will be displayed.',
          })}
        >
          <div className={'flex items-center w-[400px] gap-x-4'}>
            <div className={'flex px-3 py-1 rounded w-full bg-[#F7F8FA]'}>
              <div
                onClick={(e) => this.onChangeGoOldestCase(true)}
                className={`flex ${configuration.goOldestCase ? 'bg-white opacity-100  shadow-sm shadow-black/2 rounded-sm ' : 'opacity-50'} items-center justify-center w-1/2 px-1 py-2 text-sm cursor-pointer`}
              >
                {LanguageUtil.get({
                  id: 'pages.configuration.general.nextCaseStrategy.oldestAlert',
                  defaultMessage: 'Oldest Alert',
                })}
              </div>
              <div
                onClick={(e) => this.onChangeGoOldestCase(false)}
                className={`flex ${!configuration.goOldestCase ? 'bg-white opacity-100  shadow-sm shadow-black/2 rounded-sm ' : 'opacity-50'} items-center justify-center w-1/2 px-1 py-2 text-sm cursor-pointer`}
              >
                {LanguageUtil.get({
                  id: 'pages.configuration.general.nextCaseStrategy.highestScoredAlert',
                  defaultMessage: 'Highest Score Alert',
                })}
              </div>
            </div>
          </div>
        </GeneralConfigurationItemContainer>

        <GeneralConfigurationItemContainer
          header={LanguageUtil.get({ id: 'common.label.additionalChannel', defaultMessage: 'Additional Channel' })}
          description={LanguageUtil.get({
            id: 'pages.configuration.general.additionalChannel.description',
            defaultMessage:
              'Allows transactions in different channels to be displayed on the Transaction detail page. Also key field must be selected for each different channel.',
          })}
        >
          <span className={'flex w-full gap-x-4'}>
            <DomainConfigurationAdditionalChannelComponent />
          </span>
        </GeneralConfigurationItemContainer>
      </div>
    );
  }

  public async componentDidMount() {
    const userQuery = query.QueryBuilder.select().from(CollectionNames.USERS).where(empty()).build();
    const users: User[] = await this.repository.findMany(userQuery);
    this.setState({ users });
  }

  private onSelectTransaction = (e: FormicaSelectBoxItem) => {
    const {
      configuration: { configuration },
      changeConfiguration,
    } = this.props;
    if (e.value) {
      configuration.dataSource = e.value;
      changeConfiguration(configuration);
    }
  };

  private onSelectKeyField = (e: FormicaSelectBoxItem) => {
    const {
      configuration: { configuration },
      changeConfiguration,
    } = this.props;
    if (e.value) {
      configuration.keyField = e.value;
      changeConfiguration(configuration);
    }
  };

  private onSelectListsField = (e: MultiSelectChangeParams) => {
    const {
      configuration: { configuration },
      changeConfiguration,
    } = this.props;
    if (e.value) {
      configuration.blacklists = e.value;
      changeConfiguration(configuration);
    }
  };

  private onChangeSlaTimeToDone = (e: ChangeEvent<HTMLInputElement>) => {
    const {
      configuration: { configuration },
      changeConfiguration,
    } = this.props;
    let value: number | undefined = undefined;
    try {
      value = parseInt(e.target.value);
      if (isNaN(value)) {
        value = undefined;
      }
    } catch (e) {
      value = undefined;
    }
    configuration.sla.timeToDone = { type: 'minute', value: value };
    changeConfiguration(configuration);
  };

  private onChangeActionRequired = (value: boolean) => {
    const {
      configuration: { configuration },
      changeConfiguration,
    } = this.props;
    configuration.actionRequired = value;
    changeConfiguration(configuration);
  };

  private onChangeReasonRequired = (value: boolean) => {
    const {
      configuration: { configuration },
      changeConfiguration,
    } = this.props;
    configuration.reasonRequired = value;
    changeConfiguration(configuration);
  };

  private onChangeAllowAssign = (value: boolean): void => {
    const {
      configuration: { configuration },
      changeConfiguration,
    } = this.props;
    configuration.allowAssign = value;
    if (!value) {
      configuration.allowAssignUsers = [];
    } else if (!Array.isArray(configuration.allowAssignUsers)) {
      configuration.allowAssignUsers = [];
    }
    changeConfiguration(configuration);
  };

  private onSelectAllowAssignUsers = (e: MultiSelectChangeParams): void => {
    const {
      configuration: { configuration },
      changeConfiguration,
    } = this.props;
    configuration.allowAssignUsers = this.sanitizeUsernames(e.value);
    changeConfiguration(configuration);
  };

  private sanitizeUsernames = (value: unknown): string[] => {
    if (!Array.isArray(value)) {
      return [];
    }
    const uniqueUsernames: string[] = [];
    for (const username of value) {
      if (typeof username !== 'string') {
        continue;
      }
      const trimmedUsername: string = username.trim();
      if (trimmedUsername.length === 0 || uniqueUsernames.includes(trimmedUsername)) {
        continue;
      }
      uniqueUsernames.push(trimmedUsername);
    }
    return uniqueUsernames;
  };

  private onChangeAssignmentCandidatesRequired = (value: boolean) => {
    const {
      configuration: { configuration },
      changeConfiguration,
    } = this.props;
    configuration.assignmentCandidatesRequired = value;
    changeConfiguration(configuration);
  };

  private onChangeGraphDefaultFilterRequired = (value: boolean) => {
    const {
      configuration: { configuration },
      changeConfiguration,
    } = this.props;
    configuration.graphDefaultFilterRequired = value;
    changeConfiguration(configuration);
  };

  private onChangeGoOldestCase(value: boolean) {
    const {
      configuration: { configuration },
      changeConfiguration,
    } = this.props;
    configuration.goOldestCase = value;
    changeConfiguration(configuration);
  }

  private onChangeStartDate = (date: FormicaTimePickerDate) => {
    const {
      configuration: { configuration },
      changeConfiguration,
    } = this.props;
    configuration.graphDefaultFilter.startDate = date;
    changeConfiguration(configuration);
  };

  private onChangeEndDate = (date: FormicaTimePickerDate) => {
    const {
      configuration: { configuration },
      changeConfiguration,
    } = this.props;
    configuration.graphDefaultFilter.endDate = date;
    changeConfiguration(configuration);
  };

  private onSelectUsers = (e: MultiSelectChangeParams) => {
    const { users } = this.state;
    const {
      configuration: { configuration },
      changeConfiguration,
    } = this.props;
    if (e.value) {
      const assignmentCandidates = e.value.map((userName: string) => {
        const user = users.find((u) => u.username === userName);
        return {
          username: user?.username,
          riskType: user?.riskType.toUpperCase(),
        };
      });
      configuration.assignmentCandidates = assignmentCandidates;
      changeConfiguration(configuration);
    }
  };

  private onSelectBulkCloseRestrictedUsers = (e: MultiSelectChangeParams) => {
    const {
      configuration: { configuration },
      changeConfiguration,
    } = this.props;
    configuration.bulkCloseRestrictedUsers = e.value ?? [];
    changeConfiguration(configuration);
  };

  private onChangeSlaTimeToFirstAssign = (e: ChangeEvent<HTMLInputElement>) => {
    const {
      configuration: { configuration },
      changeConfiguration,
    } = this.props;
    let value: number | undefined = undefined;
    try {
      value = parseInt(e.target.value);
      if (isNaN(value)) {
        value = undefined;
      }
    } catch (e) {
      value = undefined;
    }
    configuration.sla.timeToFirstAssign = { type: 'minute', value: value };
    changeConfiguration(configuration);
  };

  private getTransactionSelectOptions = (): FormicaSelectBoxItem[] => {
    const {
      configuration: { eventItems },
    } = this.props;
    return eventItems.map((value) => ({
      label: value.id as string,
      value: value.id as string,
    }));
  };

  private getKeyFieldSelectOptions = (): FormicaSelectBoxItem[] => {
    const {
      configuration: { eventItems, configuration },
    } = this.props;
    if (configuration.dataSource == undefined) return [];
    const options = eventItems
      .find((event) => event.name == configuration.dataSource)
      ?.variables.filter((v) => v.properties.dataType == DataType.STRING)
      .map((value) => ({
        label: value.description && value.description.length > 0 ? value.description : value.name,
        value: value.name,
      }));
    return options || [];
  };

  private getUserOptions = (): FormicaSelectBoxItem[] => {
    const { users } = this.state;
    return users.map((user: User) => ({ label: user.username, value: user.username }));
  };

  private getTransactionCurrencyFieldOptions = () => {
    const keys = Object.keys(currency);
    return keys.map((value) => ({
      label: `${value}`,
      value: `${value}`,
    }));
  };

  private getTransactionAmountFieldOptions = () => {
    const {
      configuration: { eventItems, configuration },
    } = this.props;
    if (configuration.dataSource == undefined) return [];
    const items = eventItems
      .find((event) => event.name == configuration.dataSource)
      ?.variables.filter((v) => v.properties.dataType == DataType.NUMBER)
      .map((value) => ({
        label: value.description && value.description.length > 0 ? value.description : value.name,
        value: value.name,
      }));
    return items || [];
  };

  private getGraphFieldOptions = () => {
    const {
      configuration: { eventItems, configuration },
    } = this.props;
    if (configuration.dataSource == undefined) return [];
    let items = eventItems
      .find((event) => event.name == configuration.dataSource)
      ?.variables.filter((v) => v.properties.dataType == DataType.DATE)
      .map((value) => ({
        label: value.description && value.description.length > 0 ? value.description : value.name,
        value: value.name,
      }));
    items?.push({
      label: LanguageUtil.get({ id: 'common.label.createdAt', defaultMessage: 'Created At' }),
      value: 'createdAt',
    });
    return items || [];
  };

  private getListSelectOptions = () => {
    const {
      configuration: { lists },
    } = this.props;
    return lists.map((value) => ({
      label: value,
      value: value,
    }));
  };

  private onChangeTransactionAmountField = (e: FormicaSelectBoxItem | undefined) => {
    const {
      configuration: { configuration },
      changeConfiguration,
    } = this.props;
    configuration.transactionAmountField = e?.value;
    changeConfiguration(configuration);
  };

  private onChangeGraphField = (e: FormicaSelectBoxItem | undefined) => {
    const {
      configuration: { configuration },
      changeConfiguration,
    } = this.props;
    configuration.graphDefaultFilter.field = e?.value;
    changeConfiguration(configuration);
  };

  private onChangeTransactionCurrency = (e: FormicaSelectBoxItem | undefined) => {
    const {
      configuration: { configuration },
      changeConfiguration,
    } = this.props;
    configuration.transactionCurrencyField = e?.value;
    changeConfiguration(configuration);
  };

}

interface Props extends RouteProps, FormicaProps, ReduxProps {}

const mapStateToProps = (state: RootState) => ({
  configuration: state.configuration as ConfigurationState,
  filter: state.page.filter,
});
const mapDispatchToProps = {
  changeConfiguration,
  changePageFilter,
};
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

class State {
  users: User[] = [];
}

export default withFormica(connector(DomainConfigurationGeneralComponent));
